import {
  ChatAvailabilityEnum,
  ChatConversation,
  ChatDiscoveredChannel,
  ChatDiscoverMeta,
  ChatFavoriteSection,
  ChatSavedMessage,
  ChatUserDisplayMap,
  ChatUserSetting,
} from 'lib-trend-core';

/**
 * Internal Chat NgRx feature state (SPEC-2026-07-internal-chat, T-18) —
 * mirrors the attendance-panel/state pattern.
 *
 * HARD RULE (production-incident lesson): the messages of the OPEN conversation
 * NEVER live here. They are a component-local array in
 * ChatConversationComponent, hydrated once per channel and appended from the
 * socket with dedup — re-dispatching the full list per message overwrites the
 * local append and drops messages.
 */
export const CHAT_FEATURE_KEY = 'chatState';

/** Per-channel unread entry (kept live from `chat:unread:updated`). */
export interface ChatUnreadEntry {
  count: number;
  mentioned: boolean;
  manualUnread: boolean;
}

/** Per-user availability/custom status (kept live from `chat:status:updated`). */
export interface ChatStatusEntry {
  availability: ChatAvailabilityEnum;
  statusEmoji: string | null;
  statusText: string | null;
  statusExpiresAt: string | null;
}

/**
 * Pending DIRECT conversation draft — "Nova conversa" with a person that has
 * no DM channel yet. The channel only materializes server-side on the first
 * MESSAGE_SEND `{ directUserId }` (RF-01); when the next bootstrap carries a
 * conversation whose `directKey` matches, the reducer promotes the draft to
 * `activeChannelId`.
 */
export interface ChatDirectDraft {
  userId: string;
  /** sorted "userIdA:userIdB" — computed client-side only to MATCH bootstrap data */
  directKey: string;
}

/**
 * Live state of the websocket link, as the chat surfaces present it (T-25 ·
 * RNF-08):
 * - `connecting` — never connected yet (no banner: avoids a load-time flash);
 * - `online` — link up;
 * - `reconnecting` — link down while the device is online (socket.io retries);
 * - `offline` — link down and the device itself has no network.
 */
export type ChatConnectionStatus = 'connecting' | 'online' | 'reconnecting' | 'offline';

/**
 * Why the bootstrap read failed (T-25):
 * - `unavailable` — the chat backend could not be reached at all (network
 *   error, 5xx, endpoint absent). The chat surfaces degrade to a clear
 *   "unavailable" state; every other screen is untouched (AC-20);
 * - `error` — the request DID reach the backend and was refused for another
 *   reason: a plain error state with a retry.
 */
export type ChatBootstrapErrorKind = 'unavailable' | 'error';

/** Mention captured while composing — kept so a restored draft still knows ids. */
export interface ChatComposerDraftMention {
  /** User id */
  id: string;
  /** display name as inserted (`@Name` token in the content) */
  name: string;
}

/**
 * Per-conversation composer draft (T-19) — keyed by `channelId` (or
 * `direct:{userId}` while a DM draft has no channel yet) and restored when the
 * user switches back to the conversation. Session-scoped by design.
 */
export interface ChatComposerDraft {
  content: string;
  mentions: ChatComposerDraftMention[];
}

export interface ChatState {
  bootstrapLoaded: boolean;
  isLoading: boolean;
  loadError: boolean;
  conversations: ChatConversation[];
  favoriteSections: ChatFavoriteSection[];
  /** channelId → unread entry (only channels with count > 0) */
  unreadCounts: Record<string, ChatUnreadEntry>;
  /** userId → online */
  presence: Record<string, boolean>;
  /** userId → availability/custom status */
  statuses: Record<string, ChatStatusEntry>;
  /**
   * My most-used reaction emojis, most-used first (RF-11/AC-09). Authoritative
   * source is the `chat:topemojis:{userId}` zset, delivered by the bootstrap;
   * the quick reaction bar pads with defaults when it holds fewer than three.
   */
  topEmojis: string[];
  mySettings: ChatUserSetting | null;
  userDisplay: ChatUserDisplayMap;
  saved: ChatSavedMessage[];
  savedLoading: boolean;
  /**
   * Message ids I bookmarked (RF-14 / T-20). Hydrated from the saved list and
   * flipped optimistically by SAVED_TOGGLE so the message toolbar can render
   * "Salvar"/"Remover" without re-fetching. Saving is personal — this never
   * reaches other users.
   */
  savedIds: string[];
  /**
   * Thread root messageId → reply count already seen by me (T-20). Purely
   * client-side and session-scoped (the backend has no per-thread read state):
   * the main-flow chip shows an unseen accent while `threadCount` exceeds the
   * recorded value, and opening the thread panel records the current count.
   */
  threadSeen: Record<string, number>;
  discovered: ChatDiscoveredChannel[];
  discoverLoading: boolean;
  /** Pagination meta of the last discover read (page/perPage/total/hasMore). */
  discoverMeta: ChatDiscoverMeta | null;
  /** Conversation opened in the workspace (full view or popup single-pane). */
  activeChannelId: string | null;
  directDraft: ChatDirectDraft | null;
  /** conversationKey (channelId | `direct:{userId}`) → composer draft (T-19) */
  composerDrafts: Record<string, ChatComposerDraft>;
  /** Websocket link state (T-25) — drives the connection banner. */
  connectionStatus: ChatConnectionStatus;
  /** True for a few seconds after a recovery — the brief "Conectado" bar (T-25). */
  connectionRestored: boolean;
  /** Classification of the LAST bootstrap failure — cleared once data arrives (T-25). */
  bootstrapErrorKind: ChatBootstrapErrorKind | null;
  error: string | null;
}

export interface ChatAppState {
  readonly [CHAT_FEATURE_KEY]: ChatState;
}

export const initialChatState: ChatState = {
  bootstrapLoaded: false,
  isLoading: false,
  loadError: false,
  conversations: [],
  favoriteSections: [],
  unreadCounts: {},
  presence: {},
  statuses: {},
  topEmojis: [],
  mySettings: null,
  userDisplay: {},
  saved: [],
  savedLoading: false,
  savedIds: [],
  threadSeen: {},
  discovered: [],
  discoverLoading: false,
  discoverMeta: null,
  activeChannelId: null,
  directDraft: null,
  composerDrafts: {},
  connectionStatus: 'connecting',
  connectionRestored: false,
  bootstrapErrorKind: null,
  error: null,
};
